import { ReactNode, useCallback, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { styled, useTheme } from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useShiftRecurrenceEditStore } from '@libs/store/shifts';
import { useNotification } from '@libs/snackbar';
import { parseError } from '@libs/api/errors';
import { ComingSoonSpacer } from '@organisms/dataDisplay';
import { AnimatedTabPanel } from '@molecules/layout';
import { ExternalIdLabel } from '@molecules/dataDisplay';
import { Box, Divider, Flex, PrimaryPaper } from '@atoms/layout';
import { ArchivedShiftIcon, FaIcon, HotShiftIcon } from '@atoms/icons';
import { PeriodicShiftDrawerControls } from './periodicShiftDrawerControls';
import { ShiftHistory } from './shiftDetails/shiftHistoryLog';
import { ShiftBilling } from './shiftDetails/shiftBilling';
import { ShiftsList } from './shiftDetails/shiftsList';
import { Chip, Paper, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { ShiftNotes } from './shiftDetails/shiftNotes';
import { InfoBlocks, ShiftRecurrenceChip, TooltipNote } from '@atoms/dataDisplay';
import { DelayedLinearProgress } from '@molecules/feedback';
import { RecurrentShift } from '@libs/models/shifts';
import { AssigneeButton } from './assigneeButton';
import dayjs from 'dayjs';
import { ShiftRange, ShiftTypeIndicator } from '@organisms/dataDisplay/shifts/shiftType';
import { getShiftType } from '@libs/models/shifts/shift';
import { useLocationStore } from '@libs/store/locations';

type SeriesDetailsDrawerProps = {
  onSaved?: () => void;
};
export function SeriesDetailsDrawer({ onSaved }: SeriesDetailsDrawerProps) {
  const opened = useShiftRecurrenceEditStore((state) => state.seriesOpened);
  const close = useShiftRecurrenceEditStore((state) => state.closeShiftRecurrenceDrawer);

  return (
    <Drawer open={opened} onClose={close} anchor="right">
      <DrawerContent onSaved={onSaved} />
    </Drawer>
  );
}

type DrawerContentProps = {
  onSaved?: () => void;
};
function DrawerContent({ onSaved }: DrawerContentProps) {
  const theme = useTheme();
  const { showErrorIntl, showSuccess } = useNotification();
  const pageEnablingMap = {
    generalInfo: true,
    shiftNotes: true,
    shiftsBilling: false,
    shiftHistory: false,
  };
  const [tab, setTab] = useState(0);
  const [hideControls, setHideControls] = useState(false);
  const shift = useShiftRecurrenceEditStore((state) => state.data);
  const id = shift?.id;
  const loading = useShiftRecurrenceEditStore((state) => state.loading);
  const closeDrawer = useShiftRecurrenceEditStore((s) => s.closeShiftRecurrenceDrawer);
  const assignShifts = useShiftRecurrenceEditStore((s) => s.assignShifts);
  const unassignShift = useShiftRecurrenceEditStore((s) => s.unassignShift);
  const assignShift = useShiftRecurrenceEditStore((s) => s.assignShift);
  const loadShift = useShiftRecurrenceEditStore((s) => s.load);
  const { currentWorkingLocationId } = useLocationStore();
  const assignSubmitting = useShiftRecurrenceEditStore((s) => s.assignSubmitting);
  const localizationSubset = 'shifts.editSeriesDrawer';
  const isMultiple = shift && shift.allowedMultipleAssignees === true && !loading;
  const isSingle = shift && shift.allowedMultipleAssignees === false && !loading; // shift.allowedMultipleAssignees is nullable !!!
  const isArchive = shift && shift.isArchived && !loading;
  const isUrgent = shift && shift.isUrgent && !loading;
  const isShared = shift && shift.isShared && !loading;
  const hasTags = shift && shift.tags && shift.tags.length > 0 && !loading;

  const reloadShift = async () => {
    if (id) {
      await loadShift(id, 'series');
    }
  };

  const assignShiftsClick = async (selectedShifts: string[], assignee: string, isSingle: boolean) => {
    if (!shift) {
      throw new Error('EditSeriesForm: currentSeriesDetails is not defined');
    }
    try {
      if (isSingle) {
        // If only single assignee is allowed, series/rotation id will be passed
        await Promise.all(
          selectedShifts.map(async (shiftId) => {
            await assignShift(shiftId, assignee);
          }),
        );
      } else {
        // For multiple assignees, we pass the array of selected shifts
        await assignShifts(selectedShifts, assignee);
      }
      await reloadShift();
      showSuccess(<FormattedMessage id="shifts.assignSuccessful" />);
    } catch (error) {
      showErrorIntl(parseError(error, `${localizationSubset}.errors`).message);
    }
  };
  const unassignShiftClick = async (selectedShift: string, reason: string, reasonComment: string) => {
    try {
      await unassignShift(selectedShift, { reason, reasonComment });
      await reloadShift();
      showSuccess(<FormattedMessage id="shifts.unassignSuccessful" />);
    } catch (error) {
      showErrorIntl(parseError(error, `${localizationSubset}.errors`).message);
    }
  };

  return (
    <Stack width={{ xs: '300px', sm: '600px' }} pt={2} direction={'column'} spacing={1} height="100%">
      <Stack px={2} direction="row" alignItems={'center'} spacing={1} height="24px">
        {loading && (
          <>
            <Skeleton variant="circular" width={12} height={12} />
            <Skeleton variant="circular" width={12} height={12} />
            <Skeleton variant="circular" width={12} height={12} />
          </>
        )}
        {isUrgent && (
          <Tooltip title="Series is urgent" placement="top">
            <Box width={13} height={15}>
              <HotShiftIcon />
            </Box>
          </Tooltip>
        )}
        {isArchive && (
          <Tooltip title="Series is archived" placement="top">
            <Box width={13} height={15}>
              <ArchivedShiftIcon />
            </Box>
          </Tooltip>
        )}
        {isSingle && (
          <Tooltip title="Series assumes single assignee" placement="top">
            <Box width={13} height={15}>
              <FaIcon variant="duotone" name="user" height={'10px'} width={'10px'} color={theme.palette.primary.main} />
            </Box>
          </Tooltip>
        )}
        {isMultiple && (
          <Tooltip title="Series allows multiple assignees" placement="top">
            <Box width="13px" height="23px">
              <FaIcon variant="duotone" name="users" height={'15px'} width={'15px'} color="primary.main" />
            </Box>
          </Tooltip>
        )}
        {isShared && (
          <Tooltip title="Series is shared" placement="top">
            <Box width="13px" height="23px">
              <FaIcon variant="duotone" name="share-nodes" height={'13px'} width={'13px'} color="success.main" />
            </Box>
          </Tooltip>
        )}
        {hasTags && (
          <TooltipNote
            title={shift.tags.map((tag, idx) => (
              <Chip key={idx} label={tag.trim()} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
            ))}
            popoverTooltipSx={{ px: 2, maxWidth: '300px' }}
          >
            <Box width="13px" height="23px">
              <FaIcon variant="duotone" name="tags" height={'13px'} width={'13px'} color="primary.main" />
            </Box>
          </TooltipNote>
        )}
        <Box flex={1} />
        {loading ? (
          <Skeleton variant="rounded" width={80} height={20} />
        ) : (
          <ShiftRecurrenceChip variant="outlined" type="series" label={'Series'} size="small" />
        )}
        {loading ? (
          <Skeleton variant="rounded" width={120} height={20} />
        ) : shift?.externalId ? (
          <ExternalIdLabel localizationSubset={`shifts.seriesAndRotations`} externalId={shift?.externalId} />
        ) : null}
      </Stack>
      {loading ? <DelayedLinearProgress show={loading} /> : <Divider />}
      <Box px={2}>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} aria-label="shifts tabs">
          <Tab
            disabled={loading}
            label={<FormattedMessage id="shifts.shiftsTable" values={{ number: shift?.events.length ?? 0 }} />}
            {...a11yProps('table')}
          />
          <Tab disabled={loading} label={<FormattedMessage id="shifts.generalInfo" />} {...a11yProps('info')} />
          <Tab disabled={loading} label={<FormattedMessage id="shifts.notes" />} {...a11yProps('notes')} />
          <Tab disabled={loading} label={<FormattedMessage id="shifts.billing" />} {...a11yProps('billing')} />
          <Tab disabled={loading} label={<FormattedMessage id="shifts.historyLog" />} {...a11yProps('historyLog')} />
        </Tabs>
      </Box>
      <Flex height="100%" overflow="auto">
        <Box position="relative" width="100%">
          <AnimatedTabPanel value={tab} index={0}>
            <ShiftsList
              list={shift?.events}
              loading={loading || assignSubmitting}
              onAssignClick={assignShiftsClick}
              onUnassignClick={unassignShiftClick}
              onShiftChanged={reloadShift}
              locationId={currentWorkingLocationId}
              parentShift={shift}
            />
          </AnimatedTabPanel>
          <AnimatedTabPanel value={tab} index={1}>
            {shift && <GeneralInfo shift={shift} />}
          </AnimatedTabPanel>
          <AnimatedTabPanel value={tab} index={2}>
            <Flex column mx={2} py={2} height="100%" overflow="auto">
              <ShiftNotes notes={shift?.notes || []} loading={loading} />
            </Flex>
          </AnimatedTabPanel>
          <AnimatedTabPanel value={tab} index={3}>
            <ComingSoonSpacer disableContent={!pageEnablingMap.shiftsBilling}>
              <ShiftBilling shiftId={shift?.id ?? ''} />
            </ComingSoonSpacer>
          </AnimatedTabPanel>
          <AnimatedTabPanel value={tab} index={4}>
            <ComingSoonSpacer disableContent={!pageEnablingMap.shiftHistory}>
              <ShiftHistory shiftId={shift?.id ?? ''} />
            </ComingSoonSpacer>
          </AnimatedTabPanel>
        </Box>
      </Flex>

      <PeriodicShiftDrawerControls hide={hideControls || loading} onClose={closeDrawer} />
    </Stack>
  );
}

function a11yProps(name: string) {
  return {
    id: `shift-tab-${name}`,
    'aria-controls': `shift-tabpanel-${name}`,
  };
}

type GeneralInfoProps = {
  shift: RecurrentShift;
};
export function GeneralInfo({ shift }: GeneralInfoProps) {
  if (!shift) {
    return null;
  }

  return (
    <Stack direction="column" spacing={1} px={3} pb={3} pt={2}>
      <Typography variant="subtitle1">
        <FormattedMessage id="shifts.fields.assignee" />
      </Typography>

      <PrimaryPaper>
        <Panel>
          <AssigneeButton value={shift.assignees || []} disabled={true} fullWidth />
        </Panel>
      </PrimaryPaper>

      <Typography variant="subtitle1">
        <FormattedMessage id="shifts.drawers.shiftDetails.generalInfo.tags.heading" />
      </Typography>
      <PrimaryPaper>
        <Panel>
          <Stack direction={'row'}>
            {!shift.tags.length && <Typography variant="body2">No tags</Typography>}
            {shift.tags.map((tag) => (
              <Chip key={tag} label={tag} size="small" />
            ))}
          </Stack>
        </Panel>
      </PrimaryPaper>

      <ShiftDetailsInfoBlock shift={shift} />

      <LocationDetailsInfoBlock shift={shift} />

      {/* <SchedulerInfoBlock
        title={<FormattedMessage id="shifts.mainScheduler" />}
        scheduler={shift.mainScheduler}
      /> */}

      {/* <SchedulerInfoBlock
        title={<FormattedMessage id="shifts.afterHoursScheduler" />}
        scheduler={shift.afterHoursScheduler}
      /> */}
    </Stack>
  );
}

function ShiftDetailsInfoBlock({ shift }: { shift: RecurrentShift }) {
  const recurrence = shift.id.startsWith('shiftrotation-') ? 'rotation' : 'series';
  return (
    <InfoBlocks
      title={<FormattedMessage id="shifts.shiftDetails" />}
      items={[
        {
          key: 'recurrence',
          title: <FormattedMessage id="shifts.fields.recurrence" />,
          value: (
            <ShiftRecurrenceChip
              variant="outlined"
              type={recurrence}
              label={<FormattedMessage id={`shifts.recurrence.${recurrence}`} />}
              size="small"
            />
          ),
        },
        {
          key: 'shiftType',
          title: <FormattedMessage id="shifts.fields.shiftType" />,
          value: (
            <Flex alignCenter gap={1}>
              <ShiftTypeIndicator startTime={dayjs(shift.startDate)} />
              <Stack direction={'column'} maxWidth="100%">
                <Typography variant="body2" noWrap>
                  <ShiftRange
                    startTime={dayjs(shift.startDate)}
                    length={dayjs(shift.endDate).diff(dayjs(shift.startDate))}
                  />
                </Typography>
                <Typography variant="body2" noWrap>
                  <FormattedMessage
                    id="shifts.lengthAndType"
                    values={{
                      length: dayjs.duration(dayjs(shift.endDate).diff(dayjs(shift.startDate))).humanize(),
                      type: getShiftType(dayjs(shift.startDate)),
                    }}
                  />
                </Typography>
              </Stack>
            </Flex>
          ),
        },
        {
          key: 'qualification',
          title: <FormattedMessage id="talents.qualification" />,
          value: (
            <Flex gap={1} flexWrap={'wrap'}>
              {shift.qualifications.map((q) => (
                <Chip key={q.id} label={q.abbreviation} title={q.name} size="small" />
              ))}
            </Flex>
          ),
        },
        {
          key: 'shiftDate',
          title: <FormattedMessage id="shifts.fields.shiftDate" />,
          value: <FormattedDate value={dayjs(shift.startDate).toDate()} />,
        },
        {
          key: 'specialty',
          title: <FormattedMessage id="shifts.fields.specialty" />,
          value: <Typography variant="body2">{shift.specialty ? shift.specialty.title : '-'}</Typography>,
        },
      ]}
    />
  );
}

function LocationDetailsInfoBlock({ shift }: { shift: RecurrentShift }) {
  return (
    <>
      <InfoBlocks
        title={<FormattedMessage id="shifts.locationDetails" />}
        items={[
          // {
          //   key: 'locationName',
          //   title: <FormattedMessage id="shifts.fields.locationName" />,
          //   value: <Typography>{shift.location.name}</Typography>,
          // },
          // {
          //   key: 'address',
          //   title: <FormattedMessage id="shifts.fields.address" />,
          //   value: (
          //     <Typography>{[shift.location.address1, shift.location.address2].filter(Boolean).join(' ')}</Typography>
          //   ),
          // },
          {
            key: 'department',
            title: <FormattedMessage id="shifts.fields.department" />,
            value: <Typography>{shift.department || '-'}</Typography>,
          },
          {
            key: 'subDepartment',
            title: <FormattedMessage id="shifts.fields.subDepartment" />,
            value: <Typography>{shift.subDepartment || '-'}</Typography>,
          },
          // {
          //   key: 'primaryPhoneNumber',
          //   title: <FormattedMessage id="fields.primaryPhoneNumber" />,
          //   value: <Typography>{shift.locationDetails?.primaryPhoneNumber || '-'}</Typography>,
          // },
          // {
          //   key: 'secondaryPhoneNumber',
          //   title: <FormattedMessage id="fields.secondaryPhoneNumber" />,
          //   value: <Typography>{shift.locationDetails?.secondaryPhoneNumber || '-'}</Typography>,
          // },
        ]}
      />
    </>
  );
}

function SchedulerInfoBlock({
  scheduler,
  title,
}: {
  scheduler?: {
    name: string;
    jobTitle: string;
    primaryPhoneNumber: string;
    email: string;
  };
  title: ReactNode;
}) {
  return (
    <>
      <InfoBlocks
        title={title}
        items={[
          {
            key: 'name',
            title: <FormattedMessage id="common.name" />,
            value: <Typography>{scheduler?.name ?? '-'}</Typography>,
          },
          {
            key: 'jobTitle',
            title: <FormattedMessage id="fields.jobTitle" />,
            value: <Typography>{scheduler?.jobTitle ?? '-'}</Typography>,
          },
          {
            key: 'primaryPhoneNumber',
            title: <FormattedMessage id="fields.primaryPhoneNumber" />,
            value: <Typography>{scheduler?.primaryPhoneNumber ?? '-'}</Typography>,
          },
          {
            key: 'email',
            title: <FormattedMessage id="fields.email" />,
            value: <Typography>{scheduler?.email ?? '-'}</Typography>,
          },
        ]}
      />
    </>
  );
}

const Panel = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
}));
